import { useEffect } from 'react';

export type GlobalCursor =
  | 'auto'
  | 'default'
  | 'none'
  | 'pointer'
  | 'progress'
  | 'wait'
  | 'grab'
  | 'grabbing'
  | 'move'
  | 'copy'
  | 'alias'
  | 'no-drop'
  | 'not-allowed'
  | 'crosshair'
  | 'text'
  | 'vertical-text'
  | 'cell'
  | 'help'
  | 'context-menu'
  | 'zoom-in'
  | 'zoom-out'
  | 'all-scroll'
  | 'e-resize'
  | 'w-resize'
  | 'n-resize'
  | 's-resize'
  | 'ne-resize'
  | 'nw-resize'
  | 'se-resize'
  | 'sw-resize'
  | 'ew-resize'
  | 'ns-resize'
  | 'nesw-resize'
  | 'nwse-resize'
  | 'col-resize'
  | 'row-resize';

type CursorEntry = { readonly cursor: GlobalCursor };

// Shared stack so concurrent callers restore in the correct order.
const cursorStack: CursorEntry[] = [];

// A single <style> element injected into <head> to override all elements.
let styleElement: HTMLStyleElement | null = null;

function applyTop(): void {
  const top = cursorStack[cursorStack.length - 1]?.cursor;
  if (top) {
    if (!styleElement) {
      styleElement = document.createElement('style');
      document.head.appendChild(styleElement);
    }
    styleElement.textContent = `* { cursor: ${top} !important; }`;
  } else if (styleElement) {
    styleElement.remove();
    styleElement = null;
  }
}

/**
 * Overrides the cursor for all elements on the page while `active` is
 * true, restoring the previous state on deactivation or unmount. Multiple
 * concurrent callers are managed via a stack — the most-recently activated
 * cursor wins. Removal uses object identity so interleaved callers with
 * the same cursor value never remove each other's entries.
 */
export function useGlobalCursor(cursor: GlobalCursor, active: boolean): void {
  useEffect(() => {
    if (!active) return;

    const entry: CursorEntry = { cursor };
    cursorStack.push(entry);
    applyTop();

    return () => {
      // Identity match ensures we remove exactly this effect's entry.
      const idx = cursorStack.indexOf(entry);
      if (idx >= 0) cursorStack.splice(idx, 1);
      applyTop();
    };
  }, [cursor, active]);
}
